import { BlocCoordinator } from "@lib/bloc/BlocCoordinator"
import { compare, SortOrder } from "@lib/Comparable"
import { AssetCategory } from "@model/assets/AssetCategory"
import { AssetClass } from "@model/assets/AssetClass"
import { AssetMake } from "@model/assets/AssetMake"
import { AssetModel } from "@model/assets/AssetModel"
import { AssetType } from "@model/assets/AssetType"
import { Company } from "@model/company/Company"
import { CompanyAlias } from "@model/company/CompanyAlias"
import { Contact } from "@model/contacts/Contact"
import { District } from "@model/district/District"
import { Filter, FilterValue, isFilterEmptyOrContainsValue } from "@model/filters/Filter"
import { FilterPreset, FilterPresetForm, FilterPresetType } from "@model/filters/FilterPresets"
import { Group } from "@model/group/Group"
import { labelForServiceCode, ServiceCode, ServiceSchedule } from "@model/serviceRequests/ServiceRequest"
import { Site } from "@model/site/Site"
import { numberForWorkOrderStatus, WorkOrderFilter, WorkOrderStatus } from "@model/workOrders/WorkOrder"
import { CompanyBloc, CompanyState } from "@state/company/CompanyBloc"
import {
    FilterOverlayBlocCoordinator,
    FilterOverlayBlocCoordinatorMixin,
} from "@state/filters/FilterOverlayBlocCoordinatorMixin"
import { StatusBloc } from "@state/StatusBloc"
import { UserBloc } from "@state/user/UserBloc"
import { UserPreferencesBloc, UserPreferencesState } from "@state/UserPreferencesBloc"
import {
    WorkOrdersFilterOverlayBloc,
    WorkOrdersFilterOverlayState,
} from "@state/workOrders/WorkOrdersFilterOverlayBloc"
import { WorkOrdersTableBloc } from "@state/workOrders/WorkOrdersTableBloc"

type Dependencies = [WorkOrdersFilterOverlayState, UserPreferencesState, CompanyState]

type StateSelection = WorkOrdersFilterOverlayState & {
    aliases: Record<CompanyAlias, string>
    parentCompany: Company
    subCompanies: Company[]
    makes: AssetMake[]
    models: AssetModel[]
    types: AssetType[]
    categories: AssetCategory[]
    classes: AssetClass[]
    mechanics: Contact[]
    districts: District[]
    subDistricts: District[]
    units: District[]
    groups: Group[]
    sites: Site[]
    filterPresets: FilterPreset<WorkOrderFilter>[]
    serviceCodes: ServiceCode[]
    serviceSchedules: ServiceSchedule[]
}

export class WorkOrdersFilterOverlayViewModel
    extends BlocCoordinator<Dependencies, StateSelection>
    implements FilterOverlayBlocCoordinator<WorkOrderFilter>
{
    private filterOverlayMixin: FilterOverlayBlocCoordinatorMixin<WorkOrderFilter>

    constructor(
        filterOverlayBloc: WorkOrdersFilterOverlayBloc,
        userPreferencesBloc: UserPreferencesBloc,
        companyBloc: CompanyBloc,
        workOrdersTableBloc: WorkOrdersTableBloc,
        statusBloc: StatusBloc,
        userBloc: UserBloc
    ) {
        super([filterOverlayBloc, userPreferencesBloc, companyBloc])

        this.filterOverlayMixin = new FilterOverlayBlocCoordinatorMixin(
            FilterPresetType.WorkOrder,
            filterOverlayBloc,
            workOrdersTableBloc,
            userPreferencesBloc,
            statusBloc,
            userBloc
        )
    }

    transform = ([filterOverlayState, userPreferencesState, companyState]: Dependencies): StateSelection => {
        const filteredDistricts = companyState.districts.filter((it) =>
            isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.SubCompany], [it.companyId])
        )

        const filteredSubDistricts = companyState.subDistricts.filter(
            (it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.SubCompany], [it.companyId]) &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.District], [it.parentId ?? 0])
        )

        const filteredUnits = companyState.units.filter(
            (it) =>
                filteredSubDistricts.find((subDistrict) => subDistrict.id === it.parentId) !== undefined &&
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.SubDistrict], [
                    it.parentId ?? 0,
                ])
        )

        return {
            ...filterOverlayState,
            aliases: companyState.aliases,
            parentCompany: {
                id: companyState.companyId,
                name: companyState.companyName,
                parentId: -1,
                logoUrl: companyState.companyLogoUrl,
            },
            subCompanies: companyState.subCompanies,
            sites: companyState.sites,
            districts: filteredDistricts,
            subDistricts: filteredSubDistricts,
            units: filteredUnits,
            groups: companyState.groups.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.SubCompany], [it.companyId])
            ),
            makes: companyState.assetMakes,
            models: companyState.assetModels.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.Make], [it.makeId])
            ),
            types: companyState.assetTypes,
            categories: companyState.assetCategories.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.AssetType], [it.typeId])
            ),
            classes: companyState.assetClasses.filter((it) =>
                isFilterEmptyOrContainsValue(filterOverlayState.filters[WorkOrderFilter.Category], [it.categoryId])
            ),
            mechanics: companyState.settings.mechanics,
            serviceCodes: Object.values(companyState.serviceCodes)
                .filter((it) => it.associatedWithWorkOrder)
                .sort((a, b) => compare(labelForServiceCode(a), labelForServiceCode(b), SortOrder.Asc)),
            serviceSchedules: Object.values(companyState.serviceSchedules)
                .filter((it) => it.associatedWithWorkRequest)
                .sort((a, b) => compare(a.name, b.name, SortOrder.Asc)),
            preset: filterOverlayState.preset,
            filterPresets: userPreferencesState.filterPresets.workOrders.sort((a, b) =>
                compare(a.name, b.name, SortOrder.Asc)
            ),
        }
    }

    toggleOverlayVisibility = () => this.filterOverlayMixin.toggleOverlayVisibility()

    updateFilters = (filters: Record<WorkOrderFilter, Filter<WorkOrderFilter>>) =>
        this.filterOverlayMixin.updateFilters(filters)

    updateWorkOrderStatusFilter = (value: FilterValue[]) => {
        if (value.findIndex((it) => it.value === numberForWorkOrderStatus(WorkOrderStatus.Closed)) !== -1) {
            this.updateFilter(WorkOrderFilter.Overdue, [{ value: false }])
            this.updateFilter(WorkOrderFilter.DaysUntil, [{ value: "" }])
            this.updateFilter(WorkOrderFilter.HoursUntil, [{ value: "" }])
            this.updateFilter(WorkOrderFilter.MilesUntil, [{ value: "" }])
            this.filterOverlayMixin.updateFilter(
                WorkOrderFilter.Status,
                value.filter((it) => it.value == numberForWorkOrderStatus(WorkOrderStatus.Closed))
            )
        } else {
            this.filterOverlayMixin.updateFilter(WorkOrderFilter.Status, value)
        }
    }

    updateFilter = (type: WorkOrderFilter, value: FilterValue[]) => this.filterOverlayMixin.updateFilter(type, value)

    resetFilters = () => this.filterOverlayMixin.resetFilters()

    applyFilters = async () => this.filterOverlayMixin.applyFilters()

    filterPresetSelected = (id: number) => this.filterOverlayMixin.filterPresetSelected(id)

    showCreatePreset = () => this.filterOverlayMixin.showCreatePreset()

    hideCreatePreset = () => this.filterOverlayMixin.hideCreatePreset()

    createPreset = (form: FilterPresetForm) => this.filterOverlayMixin.createPreset(form)

    updatePresetFilters = () => this.filterOverlayMixin.updatePresetFilters()

    updatePresetForm = (preset: FilterPreset<WorkOrderFilter>, form: FilterPresetForm) =>
        this.filterOverlayMixin.updatePresetForm(preset, form)

    deletePreset = (preset: FilterPreset<WorkOrderFilter>) => this.filterOverlayMixin.deletePreset(preset)

    showManagePresets = () => this.filterOverlayMixin.showManagePresets()

    hideManagePresets = () => this.filterOverlayMixin.hideManagePresets()

    isSearchFilterValid = () => this.filterOverlayMixin.isSearchFilterValid()

    isSecondaryFilterValid = () => this.filterOverlayMixin.isSecondaryFilterValid()

    isTertiaryFilterValid = () => this.filterOverlayMixin.isTertiaryFilterValid()

    IsInMaintenance25Revision() {
        return this.filterOverlayMixin.IsInMaintenance25Revision()
    }
}
